import { GLOBAL_GAIN_BOOST } from '@arpeggi/audio'
import {
  AllDrum,
  ArpMediaType,
  BeatResolution,
  DEFAULT_BASS_SAMPLE,
  DEFAULT_CLAP_SAMPLE,
  DEFAULT_HAT_SAMPLE,
  DEFAULT_KICK_SAMPLE,
  DEFAULT_PIANO_SAMPLE,
  DEFAULT_PLUCK_SAMPLE,
  DEFAULT_SNARE_SAMPLE,
  hasComposition,
  LOOPBAR_CLAMP_VALUE_TICKS,
  MetronomeStates,
  PLAYHEAD_CLAMP_VALUE_TICKS,
  Scale,
  StemType,
  TICKS_PER_LOOP,
  TransportStates
} from '@arpeggi/shared'
import { toJS } from 'mobx'
import { clone, getParent, getType, Instance, onPatch, types } from 'mobx-state-tree'
import { UndoManager } from 'mst-middlewares'
import { IArpMedia } from '../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../features/arp-registry/ArpRegistry'
import { ForDirectExtend } from '../../features/mobx'
import { AudioEngine } from '../audio/engine/AudioEngine'
import { Arp2Utils } from '../client/SongUtils'
import SoundPlayer from '../components/core/SoundPlayer'
import { clampToValue } from '../components/core/Utils'
import { GeneratorMode, getGenerator } from '../generator/GeneratorMode'
import { SongGenerator } from '../generator/SongGenerator'
import { log, LogCategories } from '../utils/logger'
import { IStore } from './Root'
import { IClipStore } from './song/clip/ClipStore'
import { IDrumSubLoopStore } from './song/loop/DrumSubLoopStore'
import { getMultipleSampleAudioBuffers } from './song/sample/SampleUtils'
import { ISampleWithEffectsStore } from './song/sample/SampleWithEffectsStore'
import { deserializeSongFromStorage, SongStore } from './song/SongStore'
import { IAudioStemStore } from './song/stem/AudioStemStore'
import { IDrumStemStore } from './song/stem/DrumStemStore'
import { IInstrumentStemStore } from './song/stem/InstrumentStemStore'
import { IStemStore } from './song/StemStore'

const _StudioStore = types
  .model({
    isSidebarOpen: false,
    // Playhead is current 16th note in the song. We're preserving this
    // playhead for selected DrumSequencer or PianoRoll components
    playhead: 0,
    // The studio playhead tracks everything in units of ticks
    playheadElapsedTicks: 0,
    playheadStartPositionTicks: 0,
    // The position of the ghost playhead in ticks on the timeline
    ghostPlayheadTicks: 0,
    // the start (inclusive) and end (exclusive) position of the repeat bar in 16th notes
    repeatStart: 0,
    repeatEnd: TICKS_PER_LOOP,

    // Transport state
    transportState: TransportStates.STOPPED,

    // We need to know when the user is engaging with the timeline
    // so that we can prepare and reset the playhead
    timelineEngaged: false,
    // Metronome state
    metronome: MetronomeStates.STOPPED,
    isPlaying: false,
    repeatSection: false,
    repeatSong: true,
    updateAudio: true,
    loading: true,
    song: types.maybe(SongStore),
    songChangedSinceSave: false,
    currentGain: 1,
    selectedSample: -1,
    selectedStemIndex: -1,
    showDiscoverStemsDrawer: false,
    isRecording: false,
    // used in the selected track container for audio stems
    selectedClipIDs: types.array(types.string),
    studioLoading: false,
    // This is the currently played note from the computer keyboard
    // However we can expand this to be an array/object of notes when we want to allow multiple notes
    // This can also be used for midi keyboards
    playingNotes: types.map(types.number),
    isMouseDownOnPianoKeys: false,
    mouseAction: types.maybe(types.enumeration(['DEFAULT', 'CUT'])),
    // the selected column in the composition view, repesents the data shown in the selected track container
    selectedSection: 0,
    // all of the clips that're currently processing
    processingClips: types.map(types.string)
  })
  .named('studio')
  .views((self) => ({
    // the x position of the playhead that should be shown on timelines (in ticks)
    // Note: this must be used since studio.repeatSection may
    // cause studio.playheadStartPosition + studio.playheadElapsedTicks to not be the proper value to display
    get playheadVisiblePositionInTicks() {
      let x = self.playheadStartPositionTicks + self.playheadElapsedTicks
      if (self.repeatSection && x >= self.repeatEnd) {
        x = ((x - self.repeatStart) % (self.repeatEnd - self.repeatStart)) + self.repeatStart
      }
      return Math.floor(x)
    }
  }))
  .views((self) => ({
    get currentSection() {
      return Math.floor(self.playheadVisiblePositionInTicks / TICKS_PER_LOOP)
    },
    getStemGain(stemIndex: number) {
      return self.song.stems[stemIndex].getStemGain()
    },
    get alternatePlayer(): any {
      return (getParent(self) as IStore).alternatePlayer
    },
    get currentTimestampInSeconds() {
      if (self.song.lengthInSeconds === 0) return 0
      return (60 / self.song.tempo) * (self.playheadVisiblePositionInTicks / TICKS_PER_LOOP) * 16
    },
    get soloTrackIndex() {
      let stemIndex = -1
      self.song.stems.forEach((stem, index) => {
        if (stem.isSolo) stemIndex = index
      })
      return stemIndex
    },
    get canUndo() {
      return undoManager.canUndo
    },
    get canRedo() {
      return undoManager.canRedo
    },
    // the number of extra bars to show in the studio (gives users breathing room if the song is short)
    get extraBarsCount() {
      return self.song?.songDisplayLength === 0 ? 16 : 0
    }
  }))
  .actions((self) => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

    const soundPlayer = new SoundPlayer()

    // Gain node to apply to all notes in this stem
    let masterGain: GainNode = audioContext.createGain()

    let audioEngine: AudioEngine

    // Generators
    let generator: SongGenerator

    onPatch(self, (patch) => {
      if (self.updateAudio) {
        const path = patch.path
        if (
          [
            'arrangement',
            'tempo',
            'loops',
            'notes',
            'stemGain',
            'drumGains',
            'currentGain',
            'repeatSection',
            'autoRelease',
            'metronome',
            'isSolo',
            'clip'
          ].some((s) => path.includes(s))
        ) {
          audioEngine && audioEngine.render()
        }
      }
    })

    function afterCreate() {
      self.studioLoading = true

      setMouseAction('DEFAULT')
      resetPlayhead()
      setMasterGain(1)
      registerUndoManager()
      self.studioLoading = false
    }

    async function toggleMetronome() {
      const isActive = self.metronome === MetronomeStates.ACTIVE
      self.metronome = isActive ? MetronomeStates.STOPPED : MetronomeStates.ACTIVE
    }

    async function fetchMetronomeSample() {
      // 29 is DEFFIE Clave
      await fetchSample(29, audioEngine, 'core')
    }

    function resetPlayhead() {
      // Reset studio position
      self.isPlaying = false
      self.loading = true // Need a loader for this
      self.playhead = 0
      self.playheadElapsedTicks = 0
      self.playheadStartPositionTicks = 0
      self.ghostPlayheadTicks = 0
      self.transportState = TransportStates.STOPPED
      self.repeatStart = 0
      self.repeatEnd = TICKS_PER_LOOP
      self.loading = false
    }

    function registerUndoManager() {
      // Register the undo/redo middleware
      if (getType(self).name === 'studio') setUndoManager(self as IStudioStore)
      self.loading = false
      undoManager.clear()
    }

    async function initialize(shouldGenerateNew = false) {
      console.log(`initializing ${getType(self).name}`)
      disableAudioUpdates()
      resetPlayhead()

      if (!audioEngine) {
        audioEngine = new AudioEngine()
        await audioEngine.boot(audioContext, self as IStudioStore)
      }
      if (getType(self).name === 'studio') {
        await cacheAllSampleMetadata()
        await fetchMetronomeSample()
      }

      await fetchSamplesForCurrentSongAsync()

      generator = getGenerator(GeneratorMode.TRAP)

      self.loading = false
      undoManager.clear()
      console.log(`done initializing ${getType(self).name}`)
      enableAudioUpdates()

      if (shouldGenerateNew && self.song.stems.length === 0) {
        resetToNewSongState()
      }
    }

    function unselectStem() {
      setSelectedStemIndex(-1, false)
    }

    /**
     * Selects the first clip in the selected stem if it's an audio stem.
     * Useful when the selected stem index is changed to
     * an audio stem that contains clips, but the selected clip is undefined/isn't in that stem.
     */
    function selectFirstClipInStem() {
      self.selectedClipIDs.clear()
      const selectedStemIndex = self.selectedStemIndex
      if (selectedStemIndex < 0) return

      const selectedStem = self.song.stems[selectedStemIndex]
      if (!selectedStem || selectedStem.type() !== StemType.AUDIO) return

      const audioStem = selectedStem as IAudioStemStore
      if (audioStem.clips.length > 0) {
        self.selectedClipIDs.push(audioStem.clips[0].uniqueId)
      }
    }

    function setSelectedStemIndex(selectedStemIndex: number, updateClip: boolean = true) {
      if (selectedStemIndex > -1) setShowDiscoverStemsDrawer(false)
      self.selectedStemIndex = selectedStemIndex
      if (updateClip) selectFirstClipInStem()
    }

    function stopSample() {
      soundPlayer.stop()
    }

    /**
     * Fetches the given sample (if necessary) and plays it with SoundPlayer.
     *
     * @param onPlayStateChangedCallback Called with "true" when the sample starts playing, and "false" when it stops. Defaults to No-op.
     * @param semitoneOffset pitch to offset the playback with
     */
    async function playSample(
      sampleIndex: number,
      gain: number = 1,
      semitoneOffset: number = 0,
      onPlayStateChangedCallback: (boolean) => void = () => {},
      allowMultipleNotes: boolean = false
    ) {
      const decodedData = await fetchSample(sampleIndex, audioEngine)
      let player = soundPlayer
      // creates a temporary sound player with the shared audio context
      if (allowMultipleNotes) player = new SoundPlayer()
      player.play(decodedData, onPlayStateChangedCallback, semitoneOffset, gain)
    }

    /**
     * Fetches the given sample (if necessary) and plays it with SoundPlayer.
     *
     * @param sampleWithEffects The Sample to play.
     * @param onPlayStateChangedCallback Called with "true" when the sample starts playing, and "false" when it stops. Defaults to No-op.
     * @param note pitch to play the note at, in the global coordinate system. Defaults to "Middle C"
     */
    async function playSampleWithEffects(
      sampleWithEffects: ISampleWithEffectsStore,
      gain: number = 1,
      note: number = Arp2Utils.getMiddleC(),
      onPlayStateChangedCallback: (boolean) => void = () => {},
      allowMultipleNotes: boolean = false
    ) {
      playSample(
        sampleWithEffects.sampleIndex,
        gain,
        Arp2Utils.getNoteFromGlobal(note) - sampleWithEffects.key,
        onPlayStateChangedCallback,
        allowMultipleNotes
      )
    }

    /**
     * Returns promise of the sample's audiobuffer. The first fetches the metadata from the
     * samples Dynamo table. The promise completes when the actual audio file is fetched from S3.
     */
    async function fetchSample(
      sampleIndex: number,
      audioEngine: AudioEngine,
      renderer: 'core' | 'offline' = 'core'
    ): Promise<AudioBuffer> {
      const sample = await ArpRegistry.getByIdAsync(sampleIndex)
      return await sample.fetchAudioBuffer(renderer, audioEngine)
    }

    /**
     * Returns promise of the sample's audiobuffer. The first fetches the metadata from the
     * samples Dynamo table. The promise completes when the actual audio file is fetched from S3.
     */
    async function fetchStemAudioBuffer(
      remixStemIndex: number,
      audioEngine: AudioEngine,
      renderer: 'core' | 'offline' = 'core'
    ): Promise<AudioBuffer> {
      const stem = await ArpRegistry.getByIdAsync(remixStemIndex)
      return await stem.fetchAudioBuffer(renderer, audioEngine)
    }

    function undo() {
      disableAudioUpdates()
      updatePlayheadStartPosition()
      undoManager.canUndo && undoManager.undo()
      enableAudioUpdates()
    }

    function redo() {
      disableAudioUpdates()
      updatePlayheadStartPosition()
      undoManager.canRedo && undoManager.redo()
      enableAudioUpdates()
    }

    function disableAudioUpdates() {
      undoManager.withoutUndo(() => {
        self.updateAudio = false
      })
    }

    function enableAudioUpdates() {
      undoManager.withoutUndo(() => {
        self.updateAudio = true
        audioEngine && audioEngine.render()
        if (getType(self).name === 'studio') saveSongToLocalStorage()
      })
    }

    /**
     * The user has clicked to move the playhead. Rerenders the audio graph.
     */
    function userClickedPlayhead(playhead: number) {
      engageTimeline()
      const newPlayhead = clampToValue(playhead * 24, PLAYHEAD_CLAMP_VALUE_TICKS)
      setPlayheadStartPositionTicks(newPlayhead)
      setPlayheadInternal(playhead)
      disengageTimeline()
    }

    /**
     * User is actively dragging the playhead. Don't rerender the audio player. Caller should manually call
     * engageTimeline() when user starts dragging, and disengageTimeline() after user stops dragging.
     */
    function userDraggingPlayhead(playhead: number) {
      const newPlayhead = clampToValue(playhead * 24, PLAYHEAD_CLAMP_VALUE_TICKS)
      setPlayheadStartPositionTicks(newPlayhead)
      setPlayheadInternal(playhead)
    }

    /** This should only be called internally. */
    function setPlayheadInternal(playhead: number) {
      // Make sure to only set the playhead to integer values
      const newPlayhead = Math.floor(playhead)

      undoManager.withoutUndo(() => {
        self.playhead = newPlayhead
      })
    }

    function setPlayheadElapsedTicks(t) {
      if (!self.timelineEngaged) {
        undoManager.withoutUndo(() => {
          self.playheadElapsedTicks = t
        })

        const sixteenthNoteDivision = 48 / BeatResolution.SIXTEENTH_NOTE
        const playheadValue = Math.floor(
          (t + self.playheadStartPositionTicks) / sixteenthNoteDivision
        )

        setPlayheadInternal(playheadValue)

        if (
          self.playheadVisiblePositionInTicks / TICKS_PER_LOOP >=
          self.song.songDisplayLength + self.extraBarsCount - 0.01
        ) {
          if (self.repeatSection) {
            setPlayheadStartPositionTicks(self.playheadVisiblePositionInTicks, false)
          } else resetPlayhead()
        }
      }
    }

    /**
     * The ghost playhead should be set in a majority of cases, the most notable
     * when it shouldn't is when the song is paused
     */
    function setPlayheadStartPositionTicks(t: number, setGhostPlayhead: boolean = true) {
      undoManager.withoutUndo(() => {
        self.playheadStartPositionTicks = t
        self.playheadElapsedTicks = 0
        if (setGhostPlayhead) self.ghostPlayheadTicks = t
      })
    }

    function engageTimeline() {
      undoManager.withoutUndo(() => {
        self.timelineEngaged = true
      })
      audioEngine.render()
    }

    function disengageTimeline() {
      undoManager.withoutUndo(() => {
        self.timelineEngaged = false
      })
      audioEngine.render()
    }

    /** force rerenders the audio graph */
    function forceRender() {
      audioEngine && audioEngine.render()
    }

    function play() {
      // ;(getParent(self) as IStore).stopActiveInstance()
      if (self.playhead >= (self.song.songDisplayLength + self.extraBarsCount) * 64 - 1) return
      undoManager.withoutUndo(() => {
        self.transportState = TransportStates.PLAYING
        self.isPlaying = true
      })
      audioEngine && audioEngine.render()
    }

    function updatePlayheadStartPosition(updateGhostPlayhead: boolean = false) {
      undoManager.withoutUndo(() => {
        self.playheadStartPositionTicks = self.playheadVisiblePositionInTicks
        if (updateGhostPlayhead) self.ghostPlayheadTicks = self.playheadStartPositionTicks
        self.playheadElapsedTicks = 0
      })
    }

    function pause() {
      undoManager.withoutUndo(() => {
        self.transportState = TransportStates.PAUSED
        self.isPlaying = false
        setPlayheadStartPositionTicks(self.playheadVisiblePositionInTicks, false)
        if (audioEngine) audioEngine.reset()
        audioContext.suspend()
      })
      audioEngine && audioEngine.render()
    }

    function stop() {
      stopSample()
      undoManager.withoutUndo(() => {
        if (self.transportState === TransportStates.STOPPED) {
          setPlayheadStartPositionTicks(self.repeatSection ? self.repeatStart : 0)
        } else {
          setPlayheadStartPositionTicks(self.ghostPlayheadTicks)
        }
        self.transportState = TransportStates.STOPPED
        self.isPlaying = false
        if (audioEngine) audioEngine.reset()
        audioContext.suspend()
      })
      audioEngine && audioEngine.render()
    }

    function getMasterGainNode() {
      return masterGain
    }
    function getAudioContext() {
      return audioContext
    }
    function getAudioEngine() {
      return audioEngine
    }
    function setMasterGain(gainValue) {
      masterGain.gain.value = gainValue * GLOBAL_GAIN_BOOST
      self.currentGain = gainValue
    }
    // Creates a new stem for the given sound with the correct stem type.
    function addNewStemForSample(sampleIndex: number) {
      const arp = ArpRegistry.retrieveFromCache(sampleIndex)
      if (arp?.type === ArpMediaType.STEM) {
        return addAudioStemFromStem(sampleIndex)
      }
      if (AllDrum.includes(arp?.category)) {
        // Check if drum stem already exists.
        let drumStemIndex = -1
        self.song.stems.forEach((stem, index) => {
          const isDrums = stem.stemType === StemType.DRUMS
          if (isDrums && !stem.isRemixed) {
            drumStemIndex = index
          }
        })
        // If drum stem exists, add this sample to a new drum stem. Otherwise, create new stem.
        if (drumStemIndex >= 0) {
          addDrumSampleToStem(sampleIndex, drumStemIndex)
        } else {
          addDrumStem(sampleIndex)
        }
      } else {
        addInstrumentStem(sampleIndex)
      }
      if (self.song?.stems?.length === 1) {
        self.song.addLoopToStem(0)
        self.song.updateLoopForStem(0, 0, 1)
      }
    }
    // Adds a clip to an audio stem from a given sample
    async function addClipFromSampleToAudioStem(
      sampleIndex: number,
      stemIndex: number,
      start48n: number = 0
    ) {
      const stem = self.song.stems[stemIndex]
      if (!stem || stem.type() !== StemType.AUDIO) return
      const audioStem = stem as IAudioStemStore
      await fetchSample(sampleIndex, audioEngine, 'core')
      await audioStem.addClipFromSample(sampleIndex, self.song.tempo, start48n)
      await fetchSamplesForCurrentSongAsync()
      updatePlayheadStartPosition()
      enableAudioUpdates()
    }
    // Adds a clip to an audio stem from a given stem
    async function addClipFromStemToAudioStem(
      stemToRemixIndex: number,
      audioStemIndex: number,
      start48n: number = 0
    ) {
      const stem = self.song.stems[audioStemIndex]
      if (!stem || stem.type() !== StemType.AUDIO) return
      const audioStem = stem as IAudioStemStore
      await fetchStemAudioBuffer(stemToRemixIndex, audioEngine, 'core')
      await audioStem.addClipFromStem(stemToRemixIndex, self.song.tempo, start48n)
      await fetchSamplesForCurrentSongAsync()
      updatePlayheadStartPosition()
      enableAudioUpdates()
    }

    function addClipToStem(stemIndex: number, clip: IClipStore) {
      const stem = self.song.stems[stemIndex]
      if (stem.type() !== StemType.AUDIO) return
      disableAudioUpdates()
      const audioStem = stem as IAudioStemStore
      audioStem.addClip(clip)
      fetchSamplesForCurrentSongAsync().then(() => {
        updatePlayheadStartPosition()
        enableAudioUpdates()
      })
    }

    function moveClipToStem(clipID: string, stemIndex: number) {
      self.song.moveClipToStem(clipID, stemIndex)
      fetchSamplesForCurrentSongAsync().then(() => {
        updatePlayheadStartPosition()
        enableAudioUpdates()
      })
    }

    function addInstrumentStem(sampleIndex: number) {
      disableAudioUpdates()
      const category = ArpRegistry.retrieveFromCache(sampleIndex)?.category
      const sampleWithEffects = Arp2Utils.createSampleWithEffects(sampleIndex, category)
      self.song.addInstrumentStem(sampleWithEffects)
      setSelectedStemIndex(self.song.stems.length - 1)
      fetchSamplesForCurrentSongAsync().then(() => {
        updatePlayheadStartPosition()
        enableAudioUpdates()
      })
    }
    function addAudioStem() {
      disableAudioUpdates()
      self.song.addAudioStem()
      setSelectedStemIndex(self.song.stems.length - 1)
      fetchSamplesForCurrentSongAsync().then(() => {
        updatePlayheadStartPosition()
        enableAudioUpdates()
      })
    }
    function addDrumStem(sampleIndex: number) {
      disableAudioUpdates()
      const category = ArpRegistry.retrieveFromCache(sampleIndex)?.category
      const sampleWithEffects = Arp2Utils.createSampleWithEffects(sampleIndex, category)
      self.song.addDrumStem(sampleWithEffects)
      // Auto-select the newly created track
      setSelectedStemIndex(self.song.stems.length - 1)
      fetchSamplesForCurrentSongAsync().then(() => {
        updatePlayheadStartPosition()
        enableAudioUpdates()
      })
    }
    async function addDrumSampleToStem(sampleIndex: number, stemIndex: number) {
      disableAudioUpdates()
      const arp = ArpRegistry.retrieveFromCache(sampleIndex)
      if (!arp) return enableAudioUpdates()
      const category = arp?.category
      self.song.addDrumSampleToStem(
        Arp2Utils.createSampleWithEffects(sampleIndex, category),
        stemIndex
      )
      // Auto-select the newly created track
      setSelectedStemIndex(stemIndex)
      await fetchSamplesForCurrentSongAsync()
      updatePlayheadStartPosition()
      enableAudioUpdates()
    }
    /** This adds the stem as a clip in a new audio track. */
    function addAudioStemFromStem(arpIndex: number) {
      disableAudioUpdates()
      addAudioStem()
      addClipFromStemToAudioStem(arpIndex, self.song.stems.length - 1, 0)
      enableAudioUpdates()
    }
    /** This adds the stem to the song -- either as a MIDI stem or an audio stem, if appropriate. */
    function addStemFromRemix(
      stem: IArpMedia,
      setArrangementABC: boolean = true,
      autoTranspose: boolean = true
    ) {
      disableAudioUpdates()
      const isAudioStem =
        !hasComposition(stem.asInternalArpMedia()) || stem.getStemStore().type() === StemType.AUDIO
      if (isAudioStem) {
        addAudioStemFromStem(stem.id)
      } else {
        self.song.addStemFromRemix(stem, setArrangementABC, autoTranspose)
      }
      fetchSamplesForCurrentSongAsync().then(() => {
        updatePlayheadStartPosition()
        enableAudioUpdates()
      })
    }
    // This should just clear the song.
    function clearSong() {
      stop()
      disableAudioUpdates()
      unselectStem()
      const songName = self.song.name
      const song = SongStore.create()
      self.song = song
      self.song.setSongName(songName)
      undoManager.withoutUndo(() => {
        self.song.stems.forEach((stem, index) => {
          for (let i = 0; i < 16; i++) stem.stemSettings.extendArrangement()
        })
      })
      enableAudioUpdates()
    }

    function clearSongAndName() {
      clearSong()
      self.song.name = ''
    }

    /**
     * Clears the studio and adds a couple of empty stems.
     * Note: this requires that all sample metadata has been fetched (fetchAllSampleMetadata())
     */
    function resetToNewSongState() {
      clearSongAndName()
      disableAudioUpdates()

      // Add a piano or pluck loop
      // TODO a better more flexible way to do this is with generator.generateSong(self.song), but that needs some work.
      if (Math.random() < 0.5) {
        addNewStemForSample(DEFAULT_PIANO_SAMPLE)
      } else {
        addNewStemForSample(DEFAULT_PLUCK_SAMPLE)
      }
      self.song.addLoopToStem(0)
      self.song.updateLoopForStem(0, 0, 1)
      self.song.stems[0].loops[0].melodicLoop.melodicSettings.scale = Scale.MINOR
      randomizeStem(0)

      // Add a drum loop
      addNewStemForSample(DEFAULT_KICK_SAMPLE)
      addNewStemForSample(DEFAULT_HAT_SAMPLE)
      addNewStemForSample(DEFAULT_SNARE_SAMPLE)
      addNewStemForSample(DEFAULT_CLAP_SAMPLE)
      self.song.addLoopToStem(1)
      self.song.updateLoopForStem(1, 0, 1)
      randomizeStem(1)

      addNewStemForSample(DEFAULT_BASS_SAMPLE)
      self.song.addLoopToStem(2)
      self.song.updateLoopForStem(2, 0, 1)
      self.song.stems[2].loops[0].melodicLoop.melodicSettings.scale = Scale.MINOR
      randomizeStem(2)

      // Loop the first section
      loopSection(0)

      fetchSamplesForCurrentSongAsync().then(() => {
        enableAudioUpdates()
      })
      setSelectedStemIndex(0)
    }

    function randomizeSong() {
      undoManager.withoutUndo(() => {
        disableAudioUpdates()
        const songName = self.song.name
        self.song = SongStore.create()
        unselectStem()
        self.song.generateRandom(generator)
        self.song.name = songName
        fetchSamplesForCurrentSongAsync().then(() => {
          enableAudioUpdates()
        })
      })
    }

    function randomizeStem(stemIndex: number) {
      const stem = self.song.stems[stemIndex]
      if (stem.type() === StemType.AUDIO) return
      disableAudioUpdates()
      ;(stem as IInstrumentStemStore | IDrumStemStore).randomize(generator)
      fetchSamplesForCurrentSongAsync().then(() => {
        enableAudioUpdates()
      })
    }

    function randomizeLoop(stemIndex: number, loopNumber: number) {
      const stem = self.song.stems[stemIndex]
      if (stem.type() === StemType.AUDIO) return
      disableAudioUpdates()
      ;(stem as IInstrumentStemStore | IDrumStemStore).randomizeLoop(generator, loopNumber)
      fetchSamplesForCurrentSongAsync().then(() => {
        enableAudioUpdates()
      })
    }

    function clearLoop(stemIndex: number, loopNumber: number) {
      const stem = self.song.stems[stemIndex]
      if (stem.type() === StemType.AUDIO) return
      disableAudioUpdates()
      ;(stem as IInstrumentStemStore | IDrumStemStore).clearLoop(loopNumber)
      fetchSamplesForCurrentSongAsync().then(() => {
        enableAudioUpdates()
      })
    }

    function addNewLoopAndRandomize(stemIndex: number, section: number) {
      disableAudioUpdates()
      self.song.addLoopToStem(stemIndex)
      updatePlayheadStartPosition()
      const loopCount = self.song.stems[stemIndex].loops.length
      self.song.updateLoopForStem(stemIndex, section, loopCount)
      randomizeLoop(stemIndex, loopCount - 1)
    }

    /** Randomizes the sample + pattern for a single subloop. */
    async function randomizeDrumSubLoop(
      stemIndex: number,
      loopIndex: number,
      subloopIndex: number
    ) {
      disableAudioUpdates()
      const stem: IDrumStemStore = self.song.stems[stemIndex] as IDrumStemStore
      stem.randomizeDrumSampleForSubloop(subloopIndex)

      const subLoop: IDrumSubLoopStore = stem.loops[loopIndex].drumLoop.subLoops[subloopIndex]
      subLoop.randomizeForSampleCategory(
        generator,
        ArpRegistry.retrieveFromCache(stem.drumSamples[subloopIndex].sampleIndex)?.category
      )
      await fetchSamplesForCurrentSongAsync()
      updatePlayheadStartPosition()
      enableAudioUpdates()
    }

    function updateMelodicStemSample(stemIndex: number, sampleIndex: number) {
      disableAudioUpdates()
      const category = ArpRegistry.retrieveFromCache(sampleIndex)?.category
      self.song.updateMelodicStemSample(
        stemIndex,
        Arp2Utils.createSampleWithEffects(sampleIndex, category)
      )
      fetchSamplesForCurrentSongAsync().then(() => {
        enableAudioUpdates()
      })
    }

    function updateDrumStemSample(stemIndex: number, sampleIndex: number, drumIndex: number) {
      disableAudioUpdates()
      const category = ArpRegistry.retrieveFromCache(sampleIndex)?.category
      self.song.updateDrumStemSample(
        stemIndex,
        Arp2Utils.createSampleWithEffects(sampleIndex, category),
        drumIndex
      )
      fetchSamplesForCurrentSongAsync().then(() => {
        enableAudioUpdates()
      })
    }
    // const scheduleSave = debounce( (stemIndex, gain) => {
    //   self.song.stems[stemIndex].setGain(gain)
    // }, 300)
    function updateStemGain(stemIndex: number, gain: number) {
      // cancel the previous onew
      // scheduleSave.cancel()
      // update value without undo
      undoManager.withoutUndo(() => {
        self.song.stems[stemIndex].setGain(gain)
      })
      // execute debounce, it will wait 300ms before executing thing
      // scheduleSave(stemIndex, gain);
    }

    function updateStemWithProtoRemix(stemIndex: number, remixableStemMetadata) {
      // The incoming stem might not have a composition, in which case, ignore.
      if (!remixableStemMetadata) {
        console.warn("Can't drop a clip stem on a midi track, ignoring.")
        return
      }

      const stemStore: IStemStore = clone(remixableStemMetadata.getStemStore())
      stemStore.updateRemixProperties(remixableStemMetadata.artist, remixableStemMetadata.songTitle)
      self.song.stems[stemIndex] = stemStore
      fetchSamplesForCurrentSongAsync()
    }

    async function fetchSamplesForCurrentSongAsync() {
      const samples = self.song.currentSamples
      const remixedAudioStems = self.song.currentRemixedAudioStems
      log.info(
        { category: LogCategories.MOBX, data: toJS(samples) },
        'Fetching samples for current song'
      )
      const arps = await ArpRegistry.getByIdsAsync([...samples, ...remixedAudioStems])

      await getMultipleSampleAudioBuffers(arps, 'core', audioEngine)
    }

    /** Asks the ArpRegistry to cache all sample metadata, and mark it as ready. Doesn't return anything. */
    async function cacheAllSampleMetadata() {
      await Promise.all([ArpRegistry.getAllSamplesAsync(), ArpRegistry.getUploadedStemsAsync()])
      ArpRegistry.setReadyForStudioUse()
    }

    function allSamplesLoaded() {
      let allLoaded = true
      self.song.currentSamples.forEach((sampleIndex) => {
        allLoaded = allLoaded && ArpRegistry.retrieveFromCache(sampleIndex)?.audioBuffer !== null
      })
      return allLoaded
    }

    async function fetchSamplesToRender(ae: AudioEngine) {
      const samples = self.song.currentSamples.map((sample) => sample.toString())
      const sampleArpMedia = await ArpRegistry.getByIdsAsync(samples)
      await getMultipleSampleAudioBuffers(sampleArpMedia, 'offline', ae)
    }

    async function loadSongFromDraft(draft) {
      reset()
      await setSong(deserializeSongFromStorage(draft.arp))
    }

    async function loadSongFromArpMedia(song: IArpMedia) {
      await setSong(deserializeSongFromStorage(song.metadata.arpeggiComposition))
    }

    async function setSong(song) {
      disableAudioUpdates()
      unselectStem()
      self.song = song
      self.songChangedSinceSave = false
      await fetchSamplesForCurrentSongAsync()
      undoManager.clear()
      enableAudioUpdates()
    }

    function setStemSolo(stemIndex: number) {
      undoManager.withoutUndo(() => {
        self.song.stems.forEach((stem, index) => {
          if (index === stemIndex) stem.solo()
          else stem.unsolo()
        })
      })
    }

    function removeStem(stemIndex: number) {
      disableAudioUpdates()
      updatePlayheadStartPosition()
      undoManager.startGroup(() => {
        self.song.removeStem(stemIndex)
        undoManager.stopGroup()
      })
      enableAudioUpdates()
    }

    function removeStemSolo() {
      undoManager.withoutUndo(() => {
        self.song.stems.forEach((stem, index) => {
          stem.unsolo()
        })
      })
    }

    function setSelectedSample(sampleIndex: number) {
      undoManager.withoutUndo(() => {
        self.selectedSample = sampleIndex
      })
    }

    function clearStemArrangement(stemIndex: number) {
      disableAudioUpdates()
      ;(self.song.stems[stemIndex] as any).clearArrangement()
      enableAudioUpdates()
    }

    function saveSongToLocalStorage(songChanged: boolean = true) {
      console.log(`${songChanged ? 'song changed, ' : ''}saving song snapshot to local storage`)
      if (songChanged) self.songChangedSinceSave = true
      if (typeof Storage !== 'undefined')
        try {
          localStorage.setItem('songState', JSON.stringify(self.song.serializeSongForStorage()))
        } catch {
          console.log('local storage unavailable')
        }
    }

    ///////////////// Repeat Bar ////////////////

    function toggleRepeatSection() {
      undoManager.withoutUndo(() => {
        const newRepeatSectionValue = !self.repeatSection
        if (self.playheadStartPositionTicks > self.repeatEnd && newRepeatSectionValue) {
          setPlayheadStartPositionTicks(self.repeatStart)
        } else {
          setPlayheadStartPositionTicks(self.playheadVisiblePositionInTicks, false)
        }
        self.repeatSection = newRepeatSectionValue
      })
    }

    // Returns true if an update did occur
    function setRepeatStart(delta48n: number, updatePlayhead: boolean = true): boolean {
      let value = self.repeatStart + delta48n

      value = clampToValue(value, LOOPBAR_CLAMP_VALUE_TICKS)

      if (value < 0) value = 0
      if (self.repeatEnd - value < TICKS_PER_LOOP / 4) value = self.repeatEnd - TICKS_PER_LOOP / 4

      const valueChanged = self.repeatStart !== value
      if (self.repeatSection && updatePlayhead && valueChanged) {
        updatePlayheadStartPosition()
      }
      self.repeatStart = value
      return valueChanged
    }

    // Returns true if an update did occur
    function setRepeatEnd(delta48n: number, updatePlayhead: boolean = true): boolean {
      let value = self.repeatEnd + delta48n

      value = clampToValue(value, LOOPBAR_CLAMP_VALUE_TICKS)

      if (value > self.song.songDisplayLength * TICKS_PER_LOOP) value = self.song.songDisplayLength
      if (value - self.repeatStart < TICKS_PER_LOOP / 4)
        value = self.repeatStart + TICKS_PER_LOOP / 4

      const valueChanged = self.repeatEnd !== value
      if (self.repeatSection && updatePlayhead && valueChanged) {
        updatePlayheadStartPosition()
      }
      self.repeatEnd = value
      return valueChanged
    }

    // Returns true if an update did occur
    function moveRepeatStartAndEnd(delta48n: number): boolean {
      let processedDelta48 = delta48n

      processedDelta48 = clampToValue(processedDelta48, LOOPBAR_CLAMP_VALUE_TICKS)

      if (self.repeatStart + delta48n < 0) processedDelta48 = 0 - self.repeatStart
      const currStart = self.repeatStart
      const currEnd = self.repeatEnd
      const startValueChanged = setRepeatStart(processedDelta48, false)
      const endValueChanged = setRepeatEnd(processedDelta48, false)
      if (self.repeatEnd - self.repeatStart !== currEnd - currStart) {
        self.repeatStart = currStart
        self.repeatEnd = currEnd
      }
      return startValueChanged || endValueChanged
    }

    function loopSection(section: number) {
      disableAudioUpdates()
      undoManager.withoutUndo(() => {
        const newStart = section * TICKS_PER_LOOP
        const newEnd = (section + 1) * TICKS_PER_LOOP

        // The order that setRepeatEnd and setRepeatStart are called
        // in matters, which is why we have this if statement -- the two functions
        // have built in cases where they don't allow the loop bar to
        // be below a certain length
        if (newEnd > self.repeatEnd) {
          setRepeatEnd(newEnd - self.repeatEnd)
          setRepeatStart(newStart - self.repeatStart)
        } else {
          setRepeatStart(newStart - self.repeatStart)
          setRepeatEnd(newEnd - self.repeatEnd)
        }
        if (!self.repeatSection) toggleRepeatSection()
      })
      enableAudioUpdates()
    }

    //////////////////// End Repeat Bar ////////////////

    function toggleRecord() {
      undoManager.withoutUndo(() => {
        self.isRecording = !self.isRecording
      })
    }

    function reset() {
      clearSong()
      resetPlayhead()
      stop()
    }

    function setSidebarOpen(value: boolean) {
      self.isSidebarOpen = value
    }

    function setSelectedClipID(ID: string | null, add: boolean = false) {
      if (ID === null) {
        self.selectedClipIDs.clear()
        return
      }
      undoManager.startGroup(() => {
        if (!add) {
          self.selectedClipIDs.clear()
        }
        if (!self.selectedClipIDs.includes(ID)) {
          self.selectedClipIDs.push(ID)
        }
        if (
          self.selectedClipIDs.length === 1 &&
          self.selectedStemIndex !== self.song.getClipParentStemIndex(self.selectedClipIDs[0]) &&
          !add
        ) {
          self.selectedStemIndex = self.song.getClipParentStemIndex(self.selectedClipIDs[0])
        }

        undoManager.stopGroup()
      })
    }

    function deleteSelectedClips() {
      undoManager.startGroup(() => {
        disableAudioUpdates()
        self.selectedClipIDs.forEach((clipID) => {
          const parentStemIndex = self.song.getClipParentStemIndex(clipID)
          const audioStem = self.song.stems[parentStemIndex] as IAudioStemStore
          audioStem.removeClipByID(clipID)
        })
        setSelectedClipID(null)
        enableAudioUpdates()
        undoManager.stopGroup()
      })
    }

    function setPlayingNoteOn(noteValue: number) {
      undoManager.withoutUndo(() => {
        self.playingNotes.set(String(noteValue), noteValue)
      })
    }

    function setPlayingNoteOff(noteValue: number) {
      undoManager.withoutUndo(() => {
        self.playingNotes.delete(String(noteValue))
      })
    }

    function setIsMouseDownOnPianoKeys(value: boolean) {
      undoManager.withoutUndo(() => {
        self.isMouseDownOnPianoKeys = value
      })
    }

    function setShowDiscoverStemsDrawer(value: boolean) {
      if (value && self.selectedStemIndex > -1) unselectStem()
      self.showDiscoverStemsDrawer = value
    }

    function setMouseAction(value: 'DEFAULT' | 'CUT') {
      if (undoManager?.withoutUndo) {
        undoManager.withoutUndo(() => {
          self.mouseAction = value
        })
      } else {
        self.mouseAction = value
      }
    }

    function setSelectedSection(value: number) {
      undoManager.withoutUndo(() => {
        self.selectedSection = value
      })
    }

    function addProcessingClip(uniqueId: string) {
      undoManager.withoutUndo(() => {
        self.processingClips.set(uniqueId, '')
      })
    }

    function removeProcessingClip(uniqueId: string) {
      undoManager.withoutUndo(() => {
        self.processingClips.delete(uniqueId)
      })
    }

    return {
      afterCreate,
      initialize,
      registerUndoManager,
      resetPlayhead,
      engageTimeline,
      disengageTimeline,
      unselectStem,
      setSelectedStemIndex,
      updateMelodicStemSample,
      updateStemGain,
      updateDrumStemSample,
      updateStemWithProtoRemix,
      loadSongFromDraft,
      loadSongFromArpMedia,
      setSong,
      addInstrumentStem,
      addAudioStem,
      addNewStemForSample,
      addClipFromSampleToAudioStem,
      addClipFromStemToAudioStem,
      addClipToStem,
      addDrumSampleToStem,
      addStemFromRemix,
      addAudioStemFromStem,
      play,
      pause,
      stop,
      toggleRepeatSection,
      fetchSamplesForCurrentSongAsync,
      fetchSample,
      allSamplesLoaded,
      getMasterGainNode,
      setMasterGain,
      getAudioContext,
      getAudioEngine,
      userSetPlayhead: userClickedPlayhead,
      userDraggingPlayhead,
      setPlayheadElapsedTicks,
      setPlayheadStartPositionTicks,
      clearSong,
      randomizeSong,
      randomizeStem,
      randomizeDrumSubLoop,
      randomizeLoop,
      setStemSolo,
      removeStem,
      removeStemSolo,
      setSelectedSample,
      enableAudioUpdates,
      disableAudioUpdates,
      updatePlayheadStartPosition,
      undo,
      redo,
      clearStemArrangement,
      saveSongToLocalStorage,
      addNewLoopAndRandomize,
      playSample,
      playSampleWithEffects,
      stopSample,
      setRepeatStart,
      setRepeatEnd,
      reset,
      fetchSamplesToRender,
      setSidebarOpen,
      resetToNewSongState,
      toggleMetronome,
      clearLoop,
      setSelectedClipID,
      setPlayingNoteOn,
      setPlayingNoteOff,
      setIsMouseDownOnPianoKeys,
      setShowDiscoverStemsDrawer,
      setMouseAction,
      moveRepeatStartAndEnd,
      loopSection,
      moveClipToStem,
      forceRender,
      fetchStemAudioBuffer,
      toggleRecord,
      deleteSelectedClips,
      setSelectedSection,
      addProcessingClip,
      removeProcessingClip
    }
  })
  .views((self) => ({
    clipIsProcessing(clip: IClipStore): boolean {
      return self.getAudioEngine().clipIsProcessing(clip)
    }
  }))

interface StudioStoreType extends ForDirectExtend<typeof _StudioStore> {}
export const StudioStore: StudioStoreType = _StudioStore

export let undoManager: any = {}
const setUndoManager = (targetStore: IStudioStore) => {
  undoManager = UndoManager.create({}, { targetStore })
}

/**
 * Studio store Instance
 */
export interface IStudioStore extends ForDirectExtend<Instance<typeof StudioStore>> {}
